import { useEffect, useRef, useState } from 'react';

/**
 * useReveal — minimal IntersectionObserver hook for slow, editorial fade-up reveals.
 * Triggers once when the element enters the viewport.
 *
 * Usage:
 *   const { ref, visible } = useReveal();
 *   <div ref={ref} className={`reveal ${visible ? 'is-visible' : ''}`}>...
 *
 * Or with stagger:
 *   <div ref={ref} className={`reveal ${visible ? 'is-visible' : ''}`} style={{ '--reveal-delay': '120ms' }}>
 */
export const useReveal = ({ threshold = 0.12, rootMargin = '0px 0px -8% 0px' } = {}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Respect reduced motion — show immediately.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return { ref, visible };
};

export default useReveal;
